import { useState, useEffect, useMemo } from 'react';
import { createHighlighterCore, type HighlighterCore, type ThemedToken, type BundledLanguage, type LanguageInput } from 'shiki';
import { createJavaScriptRegexEngine } from 'shiki/engine/javascript';
import { useTheme } from '../components/theme-provider.js';

const DARK_THEME = 'github-dark';
const LIGHT_THEME = 'github-light';

const PRELOADED_LANGS = [
  'typescript', 'javascript', 'python', 'ruby', 'go', 'rust',
  'hcl', 'yaml', 'json', 'css', 'html', 'markdown',
  'bash', 'toml', 'xml', 'sql', 'dockerfile', 'makefile',
  'php', 'java', 'c', 'cpp',
];

const LANG_IMPORTS: Record<string, () => LanguageInput> = {
  typescript: () => import('shiki/langs/typescript.mjs'),
  javascript: () => import('shiki/langs/javascript.mjs'),
  python: () => import('shiki/langs/python.mjs'),
  ruby: () => import('shiki/langs/ruby.mjs'),
  go: () => import('shiki/langs/go.mjs'),
  rust: () => import('shiki/langs/rust.mjs'),
  hcl: () => import('shiki/langs/hcl.mjs'),
  yaml: () => import('shiki/langs/yaml.mjs'),
  json: () => import('shiki/langs/json.mjs'),
  css: () => import('shiki/langs/css.mjs'),
  html: () => import('shiki/langs/html.mjs'),
  markdown: () => import('shiki/langs/markdown.mjs'),
  bash: () => import('shiki/langs/bash.mjs'),
  toml: () => import('shiki/langs/toml.mjs'),
  xml: () => import('shiki/langs/xml.mjs'),
  sql: () => import('shiki/langs/sql.mjs'),
  dockerfile: () => import('shiki/langs/dockerfile.mjs'),
  makefile: () => import('shiki/langs/makefile.mjs'),
  php: () => import('shiki/langs/php.mjs'),
  java: () => import('shiki/langs/java.mjs'),
  c: () => import('shiki/langs/c.mjs'),
  cpp: () => import('shiki/langs/cpp.mjs'),
};

let highlighterPromise: Promise<HighlighterCore> | null = null;

function getHighlighter(): Promise<HighlighterCore> {
  if (!highlighterPromise) {
    highlighterPromise = createHighlighterCore({
      themes: [
        import('shiki/themes/github-dark.mjs'),
        import('shiki/themes/github-light.mjs'),
      ],
      langs: Object.values(LANG_IMPORTS).map((fn) => fn()),
      engine: createJavaScriptRegexEngine(),
    });
  }
  return highlighterPromise;
}

export function useShiki(): HighlighterCore | null {
  const [hl, setHl] = useState<HighlighterCore | null>(null);
  useEffect(() => {
    getHighlighter()
      .then(setHl)
      .catch((err) => console.error('[shiki] Failed to load highlighter:', err));
  }, []);
  return hl;
}

export type TokenLine = ThemedToken[];

export function useTokenizedLines(code: string, lang?: string): TokenLine[] | null {
  const hl = useShiki();
  const { resolvedTheme } = useTheme();
  const theme = resolvedTheme === 'light' ? LIGHT_THEME : DARK_THEME;

  return useMemo(() => {
    if (!hl || !code) return null;
    const validLang = (lang && PRELOADED_LANGS.includes(lang) ? lang : undefined) as BundledLanguage | undefined;
    if (!validLang) return null;
    try {
      const result = hl.codeToTokens(code, { lang: validLang, theme });
      return result.tokens;
    } catch {
      return null;
    }
  }, [hl, code, lang, theme]);
}
